import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import useJsonLd from "@/hooks/useJsonLd";
import { SERVICES, SITE } from "@/data/site";

const ORIGIN =
  typeof window !== "undefined" && window.location
    ? window.location.origin
    : "https://genesisappliancerepair.com";

const ServiceDetail = ({ slug }) => {
  const { t, lang } = useLang();
  const service = SERVICES.find((s) => s.slug === slug);
  const data = service ? t(`serviceData.${service.key}`) : null;

  // BreadcrumbList JSON-LD — re-keyed per slug + lang so Google sees the localized trail.
  // Must be called unconditionally (before any early return) to satisfy rules-of-hooks.
  useJsonLd(
    service ? `breadcrumb-${slug}-${lang}` : null,
    service
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: t("nav.home"),
              item: `${ORIGIN}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: t("nav.services"),
              item: `${ORIGIN}/services`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: data.title,
              item: `${ORIGIN}${service.to}`,
            },
          ],
        }
      : null,
  );

  if (!service) return null;

  const process = t("sectionDeepDive.processSteps");
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`page-service-${slug}`}>
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Link
              to="/services"
              data-testid="back-to-services"
              className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-[#082133] font-semibold mb-6 hover:gap-3 transition-all"
            >
              <ArrowRight className="h-3.5 w-3.5 rotate-180" />
              {t("common.ourServices")}
            </Link>
            <h1 className="font-display font-semibold text-[#111111] text-[42px] sm:text-[56px] lg:text-[68px] leading-[1.04] tracking-[-0.03em]">
              {data.title}
            </h1>
            <p className="mt-5 text-[20px] text-[#082133] font-medium">
              {data.tagline}
            </p>
            <p className="mt-6 max-w-xl text-[16px] leading-relaxed text-[#4B5563]">
              {data.body}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/contact">
                <Button
                  data-testid="service-book-cta"
                  className="rounded-full h-14 px-8 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47] text-[15px] font-medium"
                >
                  {t("sectionDeepDive.bookCta")}
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="service-call-link"
                className="inline-flex items-center justify-center gap-2 rounded-full h-14 px-7 border border-[#082133]/20 bg-white text-[#082133] hover:bg-[#082133]/5 text-[15px] font-medium"
              >
                <Phone className="h-4 w-4" />
                {SITE.phone}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5]">
              <div className="absolute -top-6 -right-6 h-32 w-32 rounded-full bg-[#DCE3DE] blur-2xl opacity-70" />
              <div className="relative h-full w-full rounded-[40px] overflow-hidden shadow-[0_45px_90px_-30px_rgba(8,33,51,0.3)]">
                <img
                  src={service.image}
                  alt={data.title}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we fix */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
              {t("sectionDeepDive.whatWeFix")}
            </span>
            <h2 className="mt-4 font-display font-semibold text-[#111111] text-[32px] sm:text-[42px] leading-[1.08] tracking-[-0.025em]">
              {data.title}
            </h2>
          </div>
          <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E7EB] rounded-3xl overflow-hidden">
            {(data.issues || []).map((issue, i) => (
              <li
                key={i}
                data-testid={`service-issue-${slug}-${i}`}
                className="bg-[#FAFAFA] p-6 sm:p-7 flex items-start gap-3"
              >
                <span className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-[#082133] text-[#FAFAFA] flex-shrink-0 mt-0.5">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span className="text-[15px] text-[#111111] leading-relaxed">
                  {issue}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20">
        <div className="mb-12">
          <span className="text-[11px] uppercase tracking-[0.22em] text-[#082133] font-semibold">
            {t("sectionDeepDive.ourProcess")}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(process || []).map((p, i) => (
            <div
              key={i}
              data-testid={`process-step-${i}`}
              className="rounded-3xl bg-white border border-[#E5E7EB] p-8 hover:-translate-y-1 hover:shadow-[0_25px_50px_-20px_rgba(8,33,51,0.15)] transition-all"
            >
              <div className="font-display text-5xl font-semibold text-[#082133]/15">
                {p.n}
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold text-[#111111]">
                {p.t}
              </h3>
              <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">
                {p.b}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Other services */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display font-semibold text-[#111111] text-[28px] sm:text-[36px] leading-tight tracking-[-0.02em]">
            {t("common.ourServices")}
          </h2>
          <Link
            to="/services"
            data-testid="other-view-all"
            className="text-[#082133] text-sm font-medium inline-flex items-center gap-2 hover:gap-3 transition-all"
          >
            {t("common.viewAllServices")} <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {others.map((s) => (
            <Link
              key={s.slug}
              to={s.to}
              data-testid={`other-service-${s.slug}`}
              className="group rounded-2xl bg-white border border-[#E5E7EB] p-6 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(8,33,51,0.15)] transition-all"
            >
              <div className="aspect-[4/3] rounded-xl overflow-hidden mb-5">
                <img
                  src={s.image}
                  alt={t(`serviceData.${s.key}.title`)}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="font-display text-[17px] font-semibold text-[#111111]">
                  {t(`serviceData.${s.key}.title`)}
                </div>
                <ArrowUpRight className="h-4 w-4 text-[#082133]" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pb-24">
        <div className="rounded-[32px] border border-[#E5E7EB] bg-white px-8 sm:px-12 py-14 sm:py-20 shadow-[0_30px_60px_-30px_rgba(8,33,51,0.15)] relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-[#DCE3DE]/60 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 items-end gap-10">
            <div className="lg:col-span-8">
              <h3 className="font-display font-semibold text-[#111111] text-[28px] sm:text-[38px] leading-[1.1] tracking-[-0.02em]">
                {t("home.ctaBlockTitle")}
              </h3>
              <p className="mt-3 max-w-xl text-[#4B5563] text-[15px] leading-relaxed">
                {t("home.ctaBlockLead")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
              <Link to="/contact">
                <Button
                  data-testid="service-bottom-cta-book"
                  className="w-full rounded-full h-14 bg-[#082133] text-[#FAFAFA] hover:bg-[#0A2D47]"
                >
                  {t("common.bookOnline")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
